import React, { useCallback, useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { Mail, Eye, Send, RotateCcw, History, ChevronRight } from 'lucide-react';
import { fmtDateTime } from '../../lib/docs';
import { toast } from 'sonner';

const STATUS_COLOR = {
  sent: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  dry_run: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  error: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
  skipped: 'bg-neutral-100 text-neutral-600 ring-1 ring-neutral-200',
};

function Pill({ status }) {
  return (
    <span data-testid={`email-log-status-${status}`}
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wide ${STATUS_COLOR[status] || 'bg-neutral-100 text-neutral-600'}`}>
      {status}
    </span>
  );
}

export default function AdminEmailsPage() {
  const [tab, setTab] = useState('templates'); // templates | logs
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [ctxJson, setCtxJson] = useState('{}');
  const [preview, setPreview] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [sending, setSending] = useState(false);

  const [logs, setLogs] = useState([]);
  const [filterTemplate, setFilterTemplate] = useState('');

  const renderPreview = useCallback(async (tpl, ctx) => {
    try {
      const out = await api.adminEmailPreview({ kind: tpl.kind, ctx });
      setPreview(out);
    } catch (e) {
      toast.error('Preview failed: ' + (e?.response?.data?.detail || e.message));
    }
  }, []);

  const handleSelect = useCallback(async (tpl) => {
    setSelected(tpl);
    setCtxJson(JSON.stringify(tpl.sample_ctx || {}, null, 2));
    setPreview(null);
    await renderPreview(tpl, tpl.sample_ctx || {});
  }, [renderPreview]);

  useEffect(() => {
    (async () => {
      try {
        const tpls = await api.adminEmailTemplates();
        setTemplates(tpls);
        if (tpls.length) handleSelect(tpls[0]);
      } catch { toast.error('Could not load templates'); }
    })();
  }, [handleSelect]);

  const onRender = async () => {
    try {
      const ctx = JSON.parse(ctxJson || '{}');
      await renderPreview(selected, ctx);
    } catch (e) {
      toast.error('Invalid JSON: ' + e.message);
    }
  };

  const onTestSend = async () => {
    if (!testTo) { toast.error('Enter a recipient'); return; }
    let ctx = {};
    try { ctx = JSON.parse(ctxJson || '{}'); }
    catch (e) { toast.error('Invalid JSON'); return; }
    setSending(true);
    try {
      const res = await api.adminEmailTest({ kind: selected.kind, to: testTo, ctx });
      if (res.status === 'sent') toast.success(`Sent (id: ${res.message_id || '—'})`);
      else if (res.status === 'dry_run') toast.message('Logged as dry-run (no API key configured)');
      else if (res.status === 'error') toast.error('Send error: ' + res.error);
      else toast.message('Status: ' + res.status);
    } catch (e) {
      toast.error('Test send failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setSending(false); }
  };

  const loadLogs = useCallback(async () => {
    try {
      const params = filterTemplate ? { template: filterTemplate, limit: 200 } : { limit: 200 };
      setLogs(await api.adminEmailLogs(params));
    } catch { toast.error('Could not load logs'); }
  }, [filterTemplate]);

  useEffect(() => { if (tab === 'logs') loadLogs(); }, [tab, filterTemplate, loadLogs]);

  return (
    <AdminLayout title="Emails"
                 subtitle="Browse transactional templates, preview, send test messages and review delivery logs."
                 actions={
                   <div className="flex items-center gap-2">
                     <button data-testid="emails-tab-templates"
                             onClick={() => setTab('templates')}
                             className={`px-3 py-1.5 rounded-md text-[12.5px] font-semibold ${tab === 'templates' ? 'bg-[#14110f] text-white' : 'bg-white border border-neutral-200 text-neutral-600'}`}>
                       <Mail className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Templates
                     </button>
                     <button data-testid="emails-tab-logs"
                             onClick={() => setTab('logs')}
                             className={`px-3 py-1.5 rounded-md text-[12.5px] font-semibold ${tab === 'logs' ? 'bg-[#14110f] text-white' : 'bg-white border border-neutral-200 text-neutral-600'}`}>
                       <History className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Logs
                     </button>
                   </div>
                 }>
      {tab === 'templates' ? (
        <div className="grid grid-cols-12 gap-5">
          <aside className="col-span-12 lg:col-span-3 bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-neutral-100 text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">
              Templates ({templates.length})
            </div>
            <ul className="divide-y divide-neutral-100">
              {templates.map((t) => {
                const active = selected?.kind === t.kind;
                return (
                  <li key={t.kind}>
                    <button data-testid={`email-template-${t.kind}`}
                            onClick={() => handleSelect(t)}
                            className={`w-full text-left px-4 py-3 flex items-center gap-2.5 transition-colors ${active ? 'bg-[#fff8e3]' : 'hover:bg-neutral-50'}`}>
                      <Mail className={`w-4 h-4 ${active ? 'text-[#c89200]' : 'text-neutral-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-[13.5px] font-semibold text-[#14110f] truncate">{t.title}</p>
                        <p className="text-[11.5px] text-neutral-500 truncate">{t.from_local}@…</p>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-neutral-300" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <section className="col-span-12 lg:col-span-9 space-y-5">
            {selected && (
              <div className="bg-white border border-neutral-200 rounded-xl p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">{selected.kind}</p>
                    <h2 data-testid="email-template-title" className="text-[20px] font-extrabold text-[#14110f] mt-1">{selected.title}</h2>
                    <p className="text-[13px] text-neutral-500 mt-1 max-w-xl">{selected.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[11.5px] uppercase tracking-wider text-neutral-400 font-semibold">Subject</p>
                    <p className="text-[13.5px] font-semibold text-[#14110f] mt-1">{selected.subject}</p>
                    <p className="text-[11.5px] text-neutral-500 mt-1">From {selected.from_local}@mail.wysetribe.ca</p>
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-5 mt-5">
                  <div className="col-span-12 lg:col-span-5">
                    <label className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Context (JSON)</label>
                    <textarea data-testid="email-ctx-json"
                              value={ctxJson} onChange={(e) => setCtxJson(e.target.value)}
                              rows={14}
                              className="mt-2 w-full font-mono text-[12px] leading-5 p-3 rounded-md border border-neutral-200 bg-neutral-50 focus:outline-none focus:border-[#f5b300]" />
                    <div className="flex items-center gap-2 mt-3">
                      <button data-testid="email-render-btn" onClick={onRender}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#14110f] text-white text-[12.5px] font-semibold hover:bg-black">
                        <Eye className="w-3.5 h-3.5" />Render preview
                      </button>
                      <button data-testid="email-reset-ctx-btn"
                              onClick={() => setCtxJson(JSON.stringify(selected.sample_ctx || {}, null, 2))}
                              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white border border-neutral-200 text-neutral-600 text-[12.5px] font-semibold hover:bg-neutral-50">
                        <RotateCcw className="w-3.5 h-3.5" />Reset to sample
                      </button>
                    </div>

                    <div className="mt-5 border-t border-neutral-100 pt-4">
                      <label className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Send test to</label>
                      <div className="flex items-center gap-2 mt-2">
                        <input data-testid="email-test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                               placeholder="you@example.com"
                               className="flex-1 px-3 py-2 rounded-md border border-neutral-200 text-[13px] focus:outline-none focus:border-[#f5b300]" />
                        <button data-testid="email-test-send-btn" onClick={onTestSend} disabled={sending}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#f5b300] text-[#14110f] text-[12.5px] font-bold hover:bg-[#e0a200] disabled:opacity-50">
                          <Send className="w-3.5 h-3.5" />{sending ? 'Sending…' : 'Send test'}
                        </button>
                      </div>
                      <p className="text-[11px] text-neutral-400 mt-2">Sends a one-off email through Resend using the context above.</p>
                    </div>
                  </div>

                  <div className="col-span-12 lg:col-span-7">
                    <label className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Preview</label>
                    <div className="mt-2 border border-neutral-200 rounded-md overflow-hidden bg-white">
                      <div className="px-4 py-2.5 bg-neutral-50 border-b border-neutral-200 flex items-center justify-between">
                        <p className="text-[12px] text-neutral-700"><strong>Subject:</strong> {preview?.subject || '—'}</p>
                        <p className="text-[11.5px] text-neutral-400">{preview?.from_address || ''}</p>
                      </div>
                      <iframe data-testid="email-preview-iframe"
                              title="email-preview"
                              srcDoc={preview?.html || '<p style="padding:24px;font-family:sans-serif;color:#999">Render to preview…</p>'}
                              className="w-full h-[520px] bg-white border-0" />
                    </div>
                    {preview?.text && (
                      <details className="mt-3 bg-white border border-neutral-200 rounded-md">
                        <summary className="cursor-pointer text-[12.5px] font-semibold text-neutral-700 px-4 py-2.5">Plain-text version</summary>
                        <pre className="px-4 pb-4 text-[12px] leading-5 text-neutral-700 whitespace-pre-wrap">{preview.text}</pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 flex items-center gap-3 border-b border-neutral-100">
            <span className="text-[11.5px] uppercase tracking-wider text-neutral-500 font-semibold">Filter</span>
            <select data-testid="email-log-filter"
                    value={filterTemplate} onChange={(e) => setFilterTemplate(e.target.value)}
                    className="px-3 py-1.5 rounded-md border border-neutral-200 text-[12.5px] bg-white">
              <option value="">All templates</option>
              {templates.map((t) => <option key={t.kind} value={t.kind}>{t.title}</option>)}
            </select>
            <button data-testid="email-log-refresh-btn" onClick={loadLogs}
                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-neutral-200 text-[12.5px] text-neutral-600 hover:bg-neutral-50">
              <RotateCcw className="w-3.5 h-3.5" />Refresh
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-neutral-50 text-neutral-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-2.5">Sent at</th>
                  <th className="text-left px-5 py-2.5">Template</th>
                  <th className="text-left px-5 py-2.5">Recipient</th>
                  <th className="text-left px-5 py-2.5">Subject</th>
                  <th className="text-left px-5 py-2.5">Status</th>
                  <th className="text-left px-5 py-2.5">Provider ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {logs.length === 0 && (
                  <tr><td colSpan="6" className="px-5 py-10 text-center text-neutral-400 text-[13px]">No emails sent yet.</td></tr>
                )}
                {logs.map((log) => (
                  <tr key={log.id} data-testid={`email-log-row-${log.id}`} className="hover:bg-neutral-50/60">
                    <td className="px-5 py-3 text-[12.5px] text-neutral-500 whitespace-nowrap">{fmtDateTime(log.sent_at)}</td>
                    <td className="px-5 py-3 font-semibold text-[#14110f]">{log.template}</td>
                    <td className="px-5 py-3 text-neutral-600">{log.recipient}</td>
                    <td className="px-5 py-3 text-neutral-600 truncate max-w-[260px]">{log.subject}</td>
                    <td className="px-5 py-3"><Pill status={log.status} /></td>
                    <td className="px-5 py-3 text-[11.5px] text-neutral-400 font-mono truncate max-w-[180px]">{log.provider_message_id || (log.error ? log.error : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
